import { motion } from 'framer-motion';
import { Check, Circle, PlayCircle } from 'lucide-react';
import type { LearningPath, LearningNode, NodeContent } from '@shared/types/learning-path';

interface ProgressTrackerProps {
  path: LearningPath;
  currentNodeId: string | null;
  onNavigate: (nodeId: string) => void;
  variant: 'sidebar' | 'bar';
  nodeContents?: Map<string, NodeContent>;
  /** When true, sidebar variant renders as a narrow icon rail. */
  collapsed?: boolean;
}

export function ProgressTracker({ path, currentNodeId, onNavigate, variant, nodeContents, collapsed = false }: ProgressTrackerProps) {
  const { completedNodes, checkpointScores, totalTimeSpent } = path.progress;
  const completedSet = new Set(completedNodes);
  const totalNodes = path.nodes.length;
  const completedCount = completedNodes.length;
  const progressPercent = totalNodes > 0 ? Math.round((completedCount / totalNodes) * 100) : 0;

  const masteryScores = Object.values(checkpointScores);
  const avgMastery = masteryScores.length > 0
    ? Math.round(masteryScores.reduce((a, b) => a + b.score, 0) / masteryScores.length)
    : 0;

  if (variant === 'bar') {
    return (
      <div className="bg-dark-surface border-b border-dark-border px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-content-muted font-body">
            {completedCount} of {totalNodes} sections
          </span>
          <span className="text-xs text-content-muted font-body">{progressPercent}%</span>
        </div>
        <div className="h-1.5 rounded-pill bg-dark-subtle overflow-hidden">
          <motion.div
            className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-indigo-400"
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    );
  }

  // Collapsed sidebar variant: narrow icon rail so the full content area
  // widens but learners still see their position + can jump between sections.
  if (collapsed) {
    return (
      <div className="w-14 shrink-0 bg-dark-surface border-r border-dark-border overflow-y-auto h-full flex flex-col">
        {/* Vertical progress ring at the top */}
        <div className="p-3 border-b border-dark-border flex flex-col items-center gap-1.5">
          <div className="relative w-8 h-8">
            <svg viewBox="0 0 32 32" className="w-8 h-8 -rotate-90">
              <circle cx="16" cy="16" r="13" fill="none" stroke="#2A2F4F" strokeWidth="3" />
              <motion.circle
                cx="16" cy="16" r="13" fill="none" stroke="url(#progress-grad-gold)" strokeWidth="3"
                strokeLinecap="round"
                initial={false}
                animate={{ strokeDasharray: `${(progressPercent / 100) * 81.68} 81.68` }}
                transition={{ duration: 0.3 }}
              />
              <defs>
                <linearGradient id="progress-grad-gold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#F0A500" />
                  <stop offset="100%" stopColor="#F5B627" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[9px] font-code text-gold-500 font-bold">{progressPercent}%</span>
            </div>
          </div>
          <span className="text-[9px] text-content-dim font-body">
            {completedCount}/{totalNodes}
          </span>
        </div>

        {/* Compact node rail — dots with tooltips */}
        <nav className="flex-1 p-1.5 flex flex-col gap-0.5">
          {path.nodes.map((node, i) => {
            const isCompleted = completedSet.has(node.nodeId);
            const isCurrent = node.nodeId === currentNodeId;
            const title = getNodeTitle(node, i, nodeContents);

            return (
              <button
                key={node.nodeId}
                onClick={() => onNavigate(node.nodeId)}
                title={`${i + 1}. ${title} · ${node.estimatedDuration}m`}
                aria-label={title}
                className={`group relative w-full h-8 flex items-center justify-center rounded cursor-pointer transition-colors
                  ${isCurrent ? 'bg-indigo-500/15' : 'hover:bg-dark-hover'}
                `}
              >
                {/* Left accent bar for current node */}
                {isCurrent && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-indigo-400" />
                )}
                {isCompleted ? (
                  <Check size={14} className="text-success" />
                ) : isCurrent ? (
                  <PlayCircle size={14} className="text-indigo-400" />
                ) : (
                  <Circle size={14} className="text-content-dim group-hover:text-content-muted" />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // Sidebar variant
  return (
    <div className="w-64 shrink-0 bg-dark-surface border-r border-dark-border overflow-y-auto h-full">
      <div className="p-4 border-b border-dark-border">
        <p className="text-xs text-content-muted font-body mb-2">
          {completedCount} of {totalNodes} complete
        </p>
        <div className="h-1.5 rounded-pill bg-dark-subtle overflow-hidden mb-3">
          <motion.div
            className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-indigo-400"
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <div className="flex justify-between text-xs text-content-dim font-body">
          {masteryScores.length > 0 && <span>Mastery: {avgMastery}%</span>}
          {totalTimeSpent > 0 && <span>{totalTimeSpent} min invested</span>}
        </div>
      </div>

      <nav className="p-2">
        {path.nodes.map((node, i) => {
          const isCompleted = completedSet.has(node.nodeId);
          const isCurrent = node.nodeId === currentNodeId;
          const title = getNodeTitle(node, i, nodeContents);

          return (
            <button
              key={node.nodeId}
              onClick={() => onNavigate(node.nodeId)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded text-left text-sm font-body transition-colors cursor-pointer
                ${isCurrent ? 'bg-indigo-500/10 text-indigo-300' : ''}
                ${isCompleted && !isCurrent ? 'text-content-muted' : ''}
                ${!isCompleted && !isCurrent ? 'text-content-secondary hover:bg-dark-hover' : ''}
              `}
            >
              {isCompleted ? (
                <Check size={14} className="text-success shrink-0" />
              ) : isCurrent ? (
                <PlayCircle size={14} className="text-indigo-400 shrink-0" />
              ) : (
                <Circle size={14} className="text-content-dim shrink-0" />
              )}
              <span className="truncate flex-1">{title}</span>
              <span className="text-xs text-content-dim ml-auto shrink-0">{node.estimatedDuration}m</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function getNodeTitle(node: LearningNode, index: number, nodeContents?: Map<string, NodeContent>): string {
  // Try to get the title from generated content
  const content = nodeContents?.get(node.nodeId);
  if (content?.content?.title) {
    return content.content.title;
  }

  // Fallback: generate a readable label from node type + index
  const typeLabels: Record<string, string> = {
    learn: 'Section',
    checkpoint: 'Checkpoint',
    exercise: 'Exercise',
    reflection: 'Reflection',
    video: 'Video',
    synthesis: 'Synthesis',
  };
  const label = typeLabels[node.nodeType] ?? 'Section';
  return `${label} ${index + 1}`;
}
